import { FirebaseError } from 'firebase/app';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from 'firebase/firestore';

import { db } from '../../config';

export const fetchPaginatedCollectionByOrganizationRealtime = async <T>(
  collectionPath: string,
  organizationId: string,
  page: number,
  pageSize: number,
  orderField: keyof T,
  onData: (data: T[], totalPages: number) => void,
  onError: (error: FirebaseError) => void,
): Promise<() => void> => {
  const colRef = collection(db, collectionPath);

  // 1) Build your base filtered querys
  const baseQuery = query(
    colRef,
    where('associated_organization', '==', organizationId),
  );

  // 2) Count total matching documents
  let totalDocuments: number;
  try {
    const countSnap = await getCountFromServer(baseQuery);
    totalDocuments = countSnap.data().count;
  } catch (e) {
    const err = e instanceof FirebaseError
      ? e
      : new FirebaseError('unknown', 'Failed to get document count');
    onError(err);
    return () => { /* no‐op unsubscribe */ };
  }

  // 3) Short‐circuit if empty
  if (totalDocuments === 0) {
    onData([], 0);
    return () => { /* no‐op unsubscribe */ };
  }

  const totalPages = Math.ceil(totalDocuments / pageSize);
  if (page < 1 || page > totalPages) {
    const err = new FirebaseError(
      'invalid-argument',
      `Invalid page number: ${page}`,
    );
    onError(err);
    throw err;
  }

  // 4) If not page 1, fetch the cursor document
  let startAfterDoc: QueryDocumentSnapshot | null = null;
  if (page > 1) {
    const offset = (page - 1) * pageSize;
    try {
      const offsetSnap = await getDocs(
        query(
          baseQuery,
          orderBy(orderField as string, 'desc'),
          limit(offset),
        ),
      );
      if (offsetSnap.docs.length < offset) {
        throw new FirebaseError('not-found', `Page ${page} out of range`);
      }
      startAfterDoc = offsetSnap.docs[offsetSnap.docs.length - 1];
    } catch (e) {
      const err = e instanceof FirebaseError
        ? e
        : new FirebaseError('unknown', 'Failed to fetch pagination cursor');
      onError(err);
      return () => { /* no‐op unsubscribe */ };
    }
  }

  // 5) Build the final paginated querys
  const paginatedQuery = startAfterDoc
    ? query(
      baseQuery,
      orderBy(orderField as string, 'desc'),
      startAfter(startAfterDoc),
      limit(pageSize),
    )
    : query(
      baseQuery,
      orderBy(orderField as string, 'desc'),
      limit(pageSize),
    );

  // 6) Register your snapshot listener inside try/catch
  try {
    const unsubscribe = onSnapshot(
      paginatedQuery,
      (snap) => {
        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as unknown as T[];
        onData(data, totalPages);
      },
      (err) => {
        onError(err as FirebaseError);
      },
    );
    return unsubscribe;
  } catch (e) {
    const err = e instanceof FirebaseError
      ? e
      : new FirebaseError('unknown', 'Failed to subscribe for updates');
    onError(err);
    return () => { /* no‐op unsubscribe */ };
  }
};
